import React, { useEffect, useRef } from 'react';
import ApexCharts, { ApexOptions } from 'apexcharts';

type ChartRadialProps = {
  value: number;
  operation: string;
  total: number;
  color?: string;
  height?: number;
};

const ChartRadial: React.FC<ChartRadialProps> = ({
  value,
  operation,
  total,
  color = '#0d6efd',
  height = 150,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  const refreshChart = () => {
    if (!chartRef.current) return;

    const chart = new ApexCharts(
      chartRef.current,
      chartOptions(color, height, value, operation, total)
    );
    chart.render();
    return chart;
  };

  useEffect(() => {
    const chart = refreshChart();
    return () => {
      if (chart) chart.destroy();
    };
  }, [value, color, height, operation, total]);

  return (
    <div
      className="radial-progress-chart text-center"
      style={{ position: 'relative' }}
    >
      <div ref={chartRef}></div>
    </div>
  );
};

const chartOptions = (
  chartColor: string,
  chartHeight: number,
  value: number,
  operation: string,
  total: number
): ApexOptions => {
  const baseColor = chartColor;
  const lightColor = 'rgba(13, 110, 253, 0.15)';
  const labelColor = '#5e6278';

  return {
    series: [Math.min(Math.max(value, 0), 100)],
    chart: {
      fontFamily: 'inherit',
      height: chartHeight,
      type: 'radialBar',
      sparkline: { enabled: true },
    },
    plotOptions: {
      radialBar: {
        hollow: {
          margin: 0,
          size: '60%',
        },
        dataLabels: {
          show: true,
          name: {
            show: true,
            offsetY: -4,
            color: labelColor,
            fontSize: '10px',
            fontWeight: 500,
            // @ts-ignore
            formatter: () => operation,
          } as any,
          value: {
            show: true,
            color: baseColor,
            fontSize: '22px',
            fontWeight: 700,
            offsetY: 10,
            // @ts-ignore
            formatter: (val: number) => `${Math.round(val)}%`,
          } as any,
          total: {
            show: true,
            // @ts-ignore
            formatter: () => `${total}`,
          } as any,
        } as any,
        track: {
          background: lightColor,
          strokeWidth: '100%',
        },
      },
    },
    colors: [baseColor],
    stroke: {
      lineCap: 'round',
    },
  };
};

export default ChartRadial;
